import {
  CmsContentAboutMissionSchema,
  type CmsContentAboutMissionForm,
} from "@/modules/cms/domain/contracts";
import { FormIconSelect } from "@/shared/ui/FormIconSelect";
import {
  Button,
  FormCheckbox,
  FormInput,
  FormRoot,
  FormTextarea,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { PlusIcon, TrashIcon } from "lucide-react";
import type { Resolver, UseFormReturn } from "react-hook-form";

type AboutMissionFormProps = {
  content: CmsContentAboutMissionForm | null;
  onSubmit: (content: CmsContentAboutMissionForm) => void;
  onCancel: () => void;
  submitting?: boolean;
};

const defaultContent: CmsContentAboutMissionForm = {
  title: {},
  content: {},
  items: [],
  isActive: true,
};

export function AboutMissionForm({
  content,
  onSubmit,
  onCancel,
  submitting,
}: AboutMissionFormProps) {
  const methods = RHF.useForm<CmsContentAboutMissionForm>({
    resolver: zodResolver(
      CmsContentAboutMissionSchema,
    ) as Resolver<CmsContentAboutMissionForm>,
    defaultValues: content ?? defaultContent,
  });
  const { fields, append, remove } = RHF.useFieldArray({
    control: methods.control,
    name: "items",
  });

  return (
    <FormRoot<CmsContentAboutMissionForm>
      methods={methods as UseFormReturn<CmsContentAboutMissionForm>}
      onSubmit={onSubmit}
    >
      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <FormInput name="title.lo" label="ຫົວຂໍ້ (ລາວ)" />
          <FormInput name="title.th" label="ຫົວຂໍ້ (ໄທ)" />
          <FormInput name="title.en" label="ຫົວຂໍ້ (EN)" />
        </div>
        <div className="space-y-2">
          <span className="font-medium text-sm">ເນື້ອຫາຫຼັກ (HTML)</span>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <FormTextarea
              name="content.lo"
              label="ລາວ"
              rows={3}
              className="font-mono text-sm"
            />
            <FormTextarea
              name="content.th"
              label="ໄທ"
              rows={3}
              className="font-mono text-sm"
            />
            <FormTextarea
              name="content.en"
              label="EN"
              rows={3}
              className="font-mono text-sm"
            />
          </div>
        </div>
        <div className="space-y-2">
          <span className="font-medium text-sm">ລາຍການ (icon + ເນື້ອຫາ)</span>
          {fields.map((field, index) => (
            <div
              key={field.id}
              className="flex flex-wrap items-start gap-2 rounded border p-3"
            >
              <FormIconSelect
                name={`items.${index}.icon`}
                label="Icon (Lucide)"
                className="min-w-[140px]"
                allowedIcons={[
                  "Target",
                  "Compass",
                  "Flag",
                  "Star",
                  "Heart",
                  "Award",
                  "Trophy",
                  "Lightbulb",
                  "Rocket",
                  "Zap",
                  "Shield",
                  "CircleCheck",
                  "Sparkles",
                  "Users",
                  "Leaf",
                  "Flame",
                  "Eye",
                  "HandHeart",
                  "Globe",
                  "Scale",
                  "BookOpen",
                  "GraduationCap",
                  "BadgeCheck",
                  "ThumbsUp",
                  "Crosshair",
                ]}
              />
              <div className="grid min-w-0 flex-1 grid-cols-1 gap-2 md:grid-cols-3">
                <FormInput name={`items.${index}.content.lo`} label="ລາວ" />
                <FormInput name={`items.${index}.content.th`} label="ໄທ" />
                <FormInput name={`items.${index}.content.en`} label="EN" />
              </div>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => remove(index)}
                aria-label="ລຶບ"
              >
                <TrashIcon className="size-4" />
              </Button>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => append({ icon: "", content: {} })}
          >
            <PlusIcon className="size-4" />
            ເພີ່ມລາຍການ
          </Button>
        </div>
        <FormCheckbox name="isActive" label="ເປີດໃຊ້ງານ" />
        <div className="flex gap-2 pt-2">
          <Button type="submit" disabled={submitting}>
            {submitting ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
          </Button>
          <Button type="button" variant="outline" onClick={onCancel}>
            ຍົກເລີກ
          </Button>
        </div>
      </div>
    </FormRoot>
  );
}
